<template>
    <Modal v-if="shown"
           :title="userData && userData.id
                    ? $t('manageUser.modal.edit.title')
                    : $t('manageUser.modal.add.title')"
           @close="show(false)"
           :close-on-click-outside="false"
           :width="activeType === activeTypes.permissions ? 820 : 600"
    >
        <template v-slot:body>
            <div class="flex flex_column">
                <div v-if="userData && userData.id" class="userName secondary_color">
                    {{ userData.firstName + " " + userData.lastName }}
                </div>
                <div v-if="!onlyDetails" class="flex flex_between md-group">
                    <md-button class="flex_1"
                               :class="activeType === activeTypes.userDetails ? 'md-primary' : ''"
                               @click="changeActiveType(activeTypes.userDetails)">
                        {{ $t("manageUser.modal.userDetails") }}
                    </md-button>
                    <md-button v-if="canViewPermissions"
                               class="flex_1"
                               :class="activeType === activeTypes.permissions ? 'md-primary' : ''"
                               @click="changeActiveType(activeTypes.permissions)">
                        {{ $t("manageUser.modal.permissions") }}
                    </md-button>
                </div>
                <div class="flex flex_column flex_1">
                    <UserDetails v-show="activeType === activeTypes.userDetails"
                                 class="flex_1 margin_horizontal margin_top"
                                 :user-data="userData"
                                 :has-next="!onlyDetails && canViewPermissions"
                                 :save-only="onlyDetails"
                                 @submit="confirmSubmitUser"
                                 @disable="confirmDisableUser"
                                 @enable="confirmEnableUser"
                                 @delete="confirmDeleteUser"
                                 @next="changeActiveType(activeTypes.permissions)"
                                 @close="show(false)"
                                 ref="userDetails"
                    ></UserDetails>
                    <Permissions v-show="activeType === activeTypes.permissions"
                                 v-if="!onlyDetails && canViewPermissions"
                                 class="flex_1 margin_horizontal margin_top"
                                 :user-data="userData"
                                 :user-roles="userRoles"
                                 @submit="confirmSubmitUser"
                                 @close="show(false)"
                                 ref="permissions"
                    ></Permissions>
                </div>
            </div>
        </template>
    </Modal>
</template>

<script>

import Modal from "@/components/templates/modal/Modal";
import {traderTeamManagementApi} from "@/common/api/traderTeamManagementApi";
import UserDetails from "@/components/templates/modal/users/UserDetails";
import Permissions from "@/components/templates/modal/users/Permissions";
import {enumDef} from "@/common/enumDefs";

export default {
    props: {
        onlyDetails: { type: Boolean, default: false }
    },
    components: {
        Permissions,
        Modal,
        UserDetails
    },
    data() {
        let activeTypes = {
            userDetails: "details",
            permissions: "permissions"
        };

        return {
            shown: false,
            activeTypes: activeTypes,
            activeType: activeTypes.userDetails,
            userData: null,
            userRoles: [],
        };
    },
    computed: {
        isEditMode() {
            return this.userData && this.userData.id;
        },
        canViewPermissions() {
            if (!this.isEditMode) {
                return [enumDef.userLevel.RootUser, enumDef.userLevel.ManagerUser].includes(this.currentUser.level);
            } else {
                return this.currentUser.userId === this.userData.id
                    || this.currentUser.level < this.userData.userLevel;
            }
        }
    },
    methods: {
        show(value) {
            this.shown = value;
        },
        showUserDetails(data) {
            this.show(true);
            this.userData = data;
            this.activeType = this.activeTypes.userDetails;
        },
        showPermissions(data) {
            this.show(true);
            this.userData = data;
            this.activeType = this.activeTypes.permissions;
        },
        changeActiveType(type) {
            if (type === this.activeTypes.permissions) {
                if (!this.$refs.userDetails.validate()) {
                    return;
                }
            }

            this.activeType = type;
        },
		buildConfirmMessage() {
			if (this.isEditMode) {
				return this.$t("manageUser.edit.confirm.msg")
			}

			if (this.$refs.permissions.getGrantedUserRoles().includes(enumDef.userRole.TraderAdmin)) {
				return this.$t("manageUser.add.confirm.msg.admin")
			}

			let extraGrantedPermissions = this.$refs.permissions.getGrantedPermissions().filter(a => !this.$refs.permissions.isActionPartOfTheUsersRole(a));
			if (extraGrantedPermissions.length > 0) {
				return this.$t("manageUser.add.confirm.msg.withExtraPermissions")
			} else {
				return this.$t("manageUser.add.confirm.msg.withoutExtraPermissions")
			}
		},
        confirmSubmitUser() {
            let option = {
                title: this.isEditMode ? this.$t("manageUser.edit.confirm.title") : this.$t("manageUser.add.confirm.title"),
                msg: this.buildConfirmMessage(),
                confirmFun: this.submitUser
            };
            this.confirmMeg(option, true);
        },
        submitUser() {
            let postData = {
                ...this.$refs.userDetails.getData()
            };

            if (!this.onlyDetails && this.canViewPermissions) {
                postData = {
                    ...postData,
                    isAdmin: this.$refs.permissions.getGrantedUserRoles().includes(enumDef.userRole.TraderAdmin),
                    actions: this.$refs.permissions.getGrantedPermissionIds(),
                    restrictions: this.$refs.permissions.getLimits(),
                    buyAccounts: this.$refs.permissions.getAssignedAccounts(),
                };
            }

            let action;
            if (this.isEditMode) {
                action = traderTeamManagementApi.operation.editUser(this.userData.id, postData);
            } else {
                action = traderTeamManagementApi.operation.addUser(postData);
            }

            action
                .then(() => {
                    this.toastSuccess();
                    this.userChanged();
                }).catch(err => {
                    this.toast(err);
                });
        },
        confirmDisableUser() {
            let option = {
                title: this.$t("manageUser.disable.confirm.title"),
                msg: this.$t("manageUser.disable.confirm.msg"),
                confirmFun: this.disableUser
            };
            this.confirmMeg(option, true);
        },
        disableUser() {
            traderTeamManagementApi.operation.disableUser(this.userData.id)
                .then(() => {
                    this.toastSuccess();
                    this.userChanged();
                }).catch(err => {
                    this.toast(err);
                });
        },
        confirmEnableUser() {
            let option = {
                title: this.$t("manageUser.enable.confirm.title"),
                msg: this.$t("manageUser.enable.confirm.msg"),
                confirmFun: this.enableUser
            };
            this.confirmMeg(option, true);
        },
        enableUser() {
            traderTeamManagementApi.operation.enableUser(this.userData.id)
                .then(() => {
                    this.toastSuccess();
                    this.userChanged();
                }).catch(err => {
                    this.toast(err);
                });
        },
        confirmDeleteUser() {
            let option = {
                title: this.$t("manageUser.delete.confirm.title"),
                msg: this.$t("manageUser.delete.confirm.msg"),
                confirmFun: this.deleteUser
            };
            this.confirmMeg(option, true);
        },
        deleteUser() {
            traderTeamManagementApi.operation.deleteUser(this.userData.id)
                .then(() => {
                    this.toastSuccess();
                    this.userChanged();
                }).catch(err => {
                    this.toast(err);
                });
        },
        userChanged() {
            this.show(false);
            this.$emit('change');
        }
    },
};
</script>

<style scoped>
.userName {
    margin-top: -1.5rem;
    margin-bottom: 1rem;
}
</style>